import { Input } from '@duxton-web/ui';
import { targetUserTypes } from 'data/trident-types';
import _ from 'lodash';
import dayjs from 'dayjs';
import React from 'react';
import * as Widget from 'segmentation-widget';

import { userTypeAppModeMap } from 'omega-rtc/utils/segp-utils';
import { getCombinedSegmentValue } from 'omega-rtc/utils/smart-campaign-utils';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import './SegPWidget.scss';

interface SegPWidgetProps {
  targetUserType: string;
  onChange: Function; // string => void
  onSelect: Function; // string => void
  segmentVal: string; // it should be in the format of <namespace>.<segmentName> for non-dax, or just <segmentID> for dax
  maxVisible: number;
  enabledSegmentNSFormat: boolean;
  namespace?: string;
  showNsDropdown: boolean;
}

// SegPWidget is a wrapper around SegPWidgetInput
// it encapsulates all the holos logic for segment selection
const SegPWidget = ({
  targetUserType,
  onChange,
  onSelect,
  segmentVal,
  maxVisible,
  enabledSegmentNSFormat,
  namespace,
  showNsDropdown = true,
}: SegPWidgetProps) => {
  const defaultValue = !_.isEmpty(segmentVal)
    && _.split(segmentVal, '.', 2).length < 2
    ? `shared.${segmentVal}`
    : segmentVal;

  const appMode = userTypeAppModeMap[targetUserType];

  return (
    <React.Fragment>
      {targetUserType === targetUserTypes.dax ||
      targetUserType === targetUserTypes.moveITDax ? (
          <Input required value={(_, data) => onChange(data.value)} />
        ) : (
          <div className="segp-widget">
            <Widget.SegPWidget
              src={`${window.Grab.config.SEGMENTATION_PLATFORM_URI}/widget`}
              onSelect={(segment) => {
                /**
                 * Check if the selected segment is expired or not.
                 * If expired shows a warning message
                 */
                const expiringTime =
                  segment && segment.expiresOn && segment.expiresOn.seconds;
                const expiringDate = dayjs(expiringTime * 1000).format(
                  'YYYY-MM-DD HH:mm:ss',
                );
                const currentDate = dayjs(Date.now()).format(
                  'YYYY-MM-DD HH:mm:ss',
                );
                if (
                  segment &&
                  segment.expiresOn &&
                  currentDate > expiringDate
                ) {
                  snackBar.warning({
                    content:
                      'Segment has expired. Please choose another segment or renew the segment validity on CDP.',
                  });
                  return;
                }

                if (segment && !segment.properties) {
                  snackBar.warning({
                    content: 'Segment details are not available',
                  });
                }
                if (segment) {
                  const newSegmentVal =
                    targetUserType === targetUserTypes.pax ||
                    targetUserType === targetUserTypes.moveITPax ||
                    targetUserType === targetUserTypes.mex ||
                    targetUserType === targetUserTypes.ovo
                      ? getCombinedSegmentValue(segment)
                      : segment.segmentName;
                  const segmentSize =
                    segment.properties &&
                    Number(segment.properties.approx_count);
                  const namespace = segment.namespace;
                  const segmentName = segment.segmentName;
                  onChange(
                    newSegmentVal,
                    segmentSize,
                    namespace,
                    segmentName
                  );
                  if (onSelect) {
                    onSelect(newSegmentVal);
                  }
                }
              }}
              appMode={appMode}
              namespace={namespace}
              defaultValue={defaultValue}
              onInputChange={(value) => {
                // Handle manually entered segment values
                if (value && typeof value === 'string') {
                  const parts = value.split('.');
                  if (parts.length >= 2) {
                    const namespace = parts[0];
                    const segmentName = parts.slice(1).join('.');
                    onChange(value, undefined, namespace, segmentName);
                    if (onSelect) {
                      onSelect(value);
                    }
                  } else {
                    // Clear values if input is empty or invalid
                    onChange(undefined, undefined, undefined, undefined);
                    if (onSelect) {
                      onSelect(undefined);
                    }
                  }
                } else {
                  // Clear values if input is empty or invalid
                  onChange(undefined, undefined, undefined, undefined);
                  if (onSelect) {
                    onSelect(undefined);
                  }
                }
              }}
              maxVisible={maxVisible}
              enabledSegmentNSFormat={enabledSegmentNSFormat}
              shouldValidate
              showNsDropdown={showNsDropdown}
            />
          </div>
        )}
    </React.Fragment>
  );
};

export default SegPWidget;
